import { FC, useEffect, useRef, useState } from 'react';
import Map, { Layer, MapRef, Marker, NavigationControl, Source, ViewState, ViewStateChangeEvent } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Geometry, Point, Polygon, Feature } from 'geojson';

import { IPosition } from '../../../model';
import { useAppSelector } from '../../../store/hooks';
import { selectAppSettings } from '../../../features';
import { geoUtils } from '../../../utils';

import styles from './CoordinatesMap.module.scss';
import { AnyPaint } from 'mapbox-gl';

type Props = {
  coordinates?: IPosition;
  geometry?: Geometry;
  className?: string;
};

const createViewState = (longitude: number, latitude: number): ViewState => ({
  longitude: longitude,
  latitude: latitude,
  zoom: 16,
  bearing: 0,
  pitch: 0,
  padding: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
});

const polygonsFill: AnyPaint = {
  'fill-color': '#D13438',
  'fill-opacity': 0.3,
};

export const CoordinatesMap: FC<Props> = ({ coordinates, geometry, className }) => {
  const settings = useAppSelector(selectAppSettings);
  const mapRef = useRef<MapRef>(null);
  const [point, setPoint] = useState<{ lng: number; lat: number } | undefined>(undefined);
  const [feature, setFeature] = useState<Feature | null>(null);

  const [viewState, setViewState] = useState<ViewState | undefined>(undefined);

  useEffect(() => {
    if ((coordinates && geoUtils.validatePosition(coordinates)) || geometry?.type === 'Point') {
      const lng = coordinates?.longitude ?? (geometry as Point).coordinates[0];
      const lat = coordinates?.latitude ?? (geometry as Point).coordinates[1];
      setPoint({ lng: lng, lat: lat });
      setViewState(createViewState(lng, lat));
    } else if (geometry?.type === 'Polygon') {
      const center = geoUtils.findCenter(geometry as Polygon).coordinates;
      setViewState(createViewState(center[0], center[1]));
      setFeature({ type: 'Feature', geometry: geometry, properties: {} });
    }
  }, [coordinates, geometry]);

  const handleOnMove = (ev: ViewStateChangeEvent) => {
    setViewState(ev.viewState);
  };

  if (!settings) return null;

  return (
    <div className={`${styles.map} ${className}`}>
      <Map
        ref={mapRef}
        {...viewState}
        maxZoom={18}
        mapboxAccessToken={settings?.map.mapbox_api_token}
        mapStyle={settings?.map.style}
        onMove={handleOnMove}
      >
        <NavigationControl position='top-right' />

        {point ? <Marker longitude={point.lng} latitude={point.lat} anchor='bottom'></Marker> : null}
        {feature ? (
          <Source type='geojson' data={feature} generateId={true}>
            <Layer type='fill' paint={polygonsFill} />
          </Source>
        ) : null}
      </Map>
    </div>
  );
};
